import React, { useEffect, useState } from 'react';

import { MenuItem, Select } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import RichTextEditor from 'components/RichTextEditor';
import SkeletonLoader from 'components/SkeletonLoader';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  StyledBox,
  StyledButton,
  StyledGrid,
  StyledTooltip,
  StyledTypography,
  VerticalFlexContainer,
} from 'styles/common';

import { getMultiSelectOptionsText } from 'constants/helper';
import theme from 'constants/theme';

import { showInfoSnackbar } from 'services/snackbar';

import { MAX_CHARACTERS } from './ControlledTextField';

const LeftAlignedButton = styled(StyledButton)`
  justify-content: flex-start;
  padding-top: 0;
  padding-bottom: 0;
  background-color: white;

  &:hover {
    background-color: white;
  }
`;

const StyledSelect = styled(Select)`
  .Mui-disabled {
    color: ${(props) => props.theme.palette.text.secondary};
  }
  ${({ showError }) =>
    showError &&
    `
      fieldset{
        border: 1px solid #EF5D7E;
      }
    `};
`;

/**
 * Dropdown support single selection.
 */
export const DropDown = ({
  answerOptions,
  selectedAnswerOptions,
  submitAnswer,
  loading,
  width,
  isSubmitted,
  isRequired,
  isMandatory, // isMandatory is direct value from question object, while is required is calculated after submit
}) =>
  loading ? (
    <SkeletonLoader skeletons={['80%']} />
  ) : (
    <StyledTooltip title={selectedAnswerOptions[0]?.description || ''} placement="top">
      <StyledSelect
        variant="outlined"
        fullWidth
        displayEmpty
        defaultValue={selectedAnswerOptions && selectedAnswerOptions[0]}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          getContentAnchorEl: null,
          style: { maxWidth: width },
        }}
        SelectDisplayProps={{
          style: {
            maxHeight: '3.5rem',
            overflowY: 'auto',
            padding: '0.75rem 1.5rem 0.75rem 0.75rem',
            backgroundColor: theme.palette.background.paper,
          },
        }}
        renderValue={(value) => (
          <StyledTypography style={{ whiteSpace: 'normal' }} variant="body2">
            {value ? value.description : `Select ${isMandatory ? '*' : ''}`}
          </StyledTypography>
        )}
        onChange={({ target }) => {
          submitAnswer('rating', target.value);
        }}
        disabled={isSubmitted}
        showError={isRequired}
      >
        {answerOptions.map((option, optionIndex) => (
          <MenuItem
            // eslint-disable-next-line react/no-array-index-key
            key={`menu-option-${optionIndex}`}
            value={option}
            style={{
              borderBottom: `1px solid ${
                optionIndex !== answerOptions.length - 1 ? theme.palette.borders.lightGrey : 'transparent'
              }`,
            }}
          >
            <StyledTypography style={{ whiteSpace: 'normal' }} variant="body2">
              {option.description}
            </StyledTypography>
          </MenuItem>
        ))}
      </StyledSelect>
    </StyledTooltip>
  );

export const MultiDropDown = ({
  answerOptions,
  selectedAnswerOptions,
  submitAnswer,
  loading,
  width,
  isSubmitted,
  isRequired,
  minOptions,
  maxOptions,
  isSubmitClicked,
}) => {
  const dispatch = useDispatch();
  if (loading) {
    return <SkeletonLoader skeletons={['80%']} />;
  }

  const selectedAnswerIds = selectedAnswerOptions ? selectedAnswerOptions.map((item) => item.id) : '';
  const selectedAnswerOptionsReferential = answerOptions.filter((item) => selectedAnswerIds.indexOf(item.id) > -1);

  return (
    <VerticalFlexContainer>
      <StyledTooltip title={selectedAnswerOptionsReferential?.map((item) => item.description).join(', ') || ''}>
        <StyledSelect
          variant="outlined"
          fullWidth
          displayEmpty
          multiple
          value={selectedAnswerOptionsReferential}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            getContentAnchorEl: null,
            style: { maxWidth: width },
          }}
          SelectDisplayProps={{
            style: {
              maxHeight: '3.5rem',
              overflowY: 'auto',
              padding: '0.75rem 1.5rem 0.75rem 0.75rem',
              backgroundColor: theme.palette.background.paper,
            },
          }}
          renderValue={(selected) => {
            let text = '';
            if (selected.length === 0) {
              text = `Select ${isRequired ? '*' : ''}`;
            } else {
              text = selected.map((item) => item.description).join(', ');
            }
            return (
              <StyledTypography style={{ whiteSpace: 'normal' }} variant="body2">
                {text}
              </StyledTypography>
            );
          }}
          onChange={({ target }) => {
            if (target.value.length > maxOptions) {
              dispatch(showInfoSnackbar('Maximum allowed options already selected'));
              return;
            }
            submitAnswer('rating', target.value);
          }}
          disabled={isSubmitted}
          showError={
            (!selectedAnswerOptions || selectedAnswerOptions.length < minOptions) && isRequired && isSubmitClicked
          }
        >
          {answerOptions.map((option, optionIndex) => {
            const isSelected = selectedAnswerOptions.map((item) => item.id).indexOf(option.id) > -1;
            return (
              // eslint-disable-next-line react/no-array-index-key
              <MenuItem key={`menu-option-${optionIndex}`} value={option}>
                <Checkbox color="primary" checked={isSelected} />
                <StyledTypography
                  style={{ whiteSpace: 'normal' }}
                  variant="body2"
                  color={isSelected ? '#3a8ee9' : 'textPrimary'}
                >
                  {option.description}
                </StyledTypography>
              </MenuItem>
            );
          })}
        </StyledSelect>
      </StyledTooltip>
      <StyledTypography topGutter={1} variant="caption" color="textSecondary">
        {getMultiSelectOptionsText(minOptions, maxOptions, answerOptions.length)}
        {isRequired ? ' *' : ''}
      </StyledTypography>
    </VerticalFlexContainer>
  );
};

export const ControlledTextField = ({
  placeholder,
  defaultText,
  plainDefaulText,
  onSubmit,
  loading,
  disabled,
  isRequired,
}) => {
  const [text, setText] = useState(defaultText);
  const [plainText, setPlainText] = useState(plainDefaulText);

  const handleRTESubmit = (htmlText, plainText_) => {
    onSubmit(htmlText, plainText_);
  };

  const showError = isRequired && !plainText;

  useEffect(() => {
    setText(defaultText);
    setPlainText(plainDefaulText);
  }, [defaultText, plainDefaulText]);

  useEffect(() => {
    if (loading) {
      setText(defaultText);
      setPlainText(plainDefaulText);
    }
  }, [loading]);

  return (
    <StyledGrid container direction="column" alignItems="flex-end">
      <RichTextEditor
        placeholder={placeholder}
        initialText={text}
        plainText={plainText}
        disabled={disabled}
        maxCharLimit={MAX_CHARACTERS}
        onBlur={handleRTESubmit}
        loading={loading}
        showError={showError}
      />
    </StyledGrid>
  );
};

export const RatingSelect = ({ answerOptions, selectedAnswerOptions, submitAnswer, loading, isSubmitted }) =>
  loading ? (
    <SkeletonLoader skeletons={['40%']} />
  ) : (
    <StyledGrid container direction="column">
      {answerOptions.map((option) => {
        const { id, description, score } = option;
        return (
          <StyledGrid item xs={5} bottomGutter={2}>
            <LeftAlignedButton
              variant="outlined"
              fullWidth
              disableElevation
              disableFocusRipple
              disabled={isSubmitted}
              onClick={(event) => {
                submitAnswer(option);
                event.stopPropagation();
              }}
            >
              <StyledBox
                flex="0"
                borderRight={`2px solid ${theme.palette.borders.lightGrey}`}
                paddingTop="0.5rem"
                paddingBottom="0.5rem"
              >
                <StyledTypography variant="body2" color="primary" rightGutter={2}>
                  {score}
                </StyledTypography>
              </StyledBox>
              <StyledTypography
                variant="body2"
                leftGutter={2}
                color={
                  selectedAnswerOptions && selectedAnswerOptions.length > 0 && id === selectedAnswerOptions[0].id
                    ? 'primary'
                    : 'textPrimary'
                }
              >
                {description}
              </StyledTypography>
            </LeftAlignedButton>
          </StyledGrid>
        );
      })}
    </StyledGrid>
  );

export const DefaultAnswerComp = ({
  placeholder = '',
  defaultText,
  plainDefaulText,
  onSubmit,
  loading,
  disabled,
  showError: showErrorProp,
}) => {
  const [text, setText] = useState(defaultText);
  const [plainText, setPlainText] = useState(plainDefaulText);

  const handleRTESubmit = (htmlText, plainText_) => {
    onSubmit(htmlText, plainText_);
  };

  useEffect(() => {
    setText(defaultText);
    setPlainText(plainDefaulText);
  }, [defaultText, plainDefaulText]);

  useEffect(() => {
    if (loading) {
      setText(defaultText);
      setPlainText(plainDefaulText);
    }
  }, [loading]);

  const showError = showErrorProp && !plainText;

  return (
    <RichTextEditor
      placeholder={placeholder}
      initialText={text}
      plainText={plainText}
      disabled={disabled}
      maxCharLimit={MAX_CHARACTERS}
      onBlur={handleRTESubmit}
      loading={loading}
      showError={showError}
      containerWidth="60%"
    />
  );
};
